/**
 * Kitchen-sink seating + subtype inference for SCANNED sinks.
 *
 * RoomPlan reports a sink as a box somewhere inside (or near) its storage
 * box. The kitchen post-processor then rebuilds that storage as a standard
 * lower cabinet snapped to the wall, so a basin left at its scanned X/Z
 * routinely ends up hanging past the counter front or pushed into the wall
 * (Pittsburgh Dev Scan 1: 4.8" proud; Scan 4: 1.7"). Nothing about the scan
 * tells us where the basin really was to the inch, but the seat of a real
 * sink is fully determined by the counter it sits in — so we seat it there.
 *
 * Two subtypes are inferred from the scan alone (users are never asked
 * during a scan):
 *
 *   - `undermount` — the basin front sits UNDERMOUNT_FRONT_SETBACK behind
 *     the counter front.
 *   - `farmhouse`  — the scanned box came within FARMHOUSE_REVEAL_MAX of the
 *     scanned storage face (an apron is the only sink part RoomPlan can see
 *     near the face; a bowl sits well behind it). Its apron lands
 *     APRON_PROUD in front of the cabinet box, flush with the door plane,
 *     and its width snaps to the catalog sizes we carry (Kohler Whitehaven /
 *     Strive / Vault: 30 / 33 / 36").
 *
 * Everything here is pure and frame-agnostic: callers pass the host box, a
 * unit `frontDir` (host → room, in world XZ) and get a new center/rotation
 * back. Depth is always the host's local Z (`dimensions.z`).
 */

const { INCHES_TO_METERS: IN } = require('./constants');

/** Kohler apron-front sinks measure ~21.2" deep × ~10" tall (6488/5415/3943). */
const FARMHOUSE_SINK = { depth: 21 * IN, height: 10 * IN };
/** Nominal apron widths we carry GLBs for (29.5 / 32.5 / 35.5" actual). */
const FARMHOUSE_WIDTHS_M = [30, 33, 36].map((w) => w * IN);
/**
 * Scanned box front within this of the SCANNED host face (or past it) ⇒
 * apron. The scanned storage box includes the counter overhang, so even a
 * real apron reads an inch or two inside it: Pittsburgh Dev Scan 4's
 * farmhouse sink read 2.43"; Scan 1's undermount bowl read 7.21".
 */
const FARMHOUSE_REVEAL_MAX = 3 * IN;
/** Undermount basin front sits this far behind the counter front. */
const UNDERMOUNT_FRONT_SETBACK = 3 * IN;
/** Apron face proud of the cabinet box — the door-panel plane. */
const APRON_PROUD = 0.75 * IN;
/** Apron TOP proud of the countertop — a coplanar top z-fights the slab. */
const FARMHOUSE_TOP_PROUD = 0.25 * IN;
/**
 * The subtypes `inferKitchenSinkSubtype` can reach from a scan alone. A sink
 * already carrying one of these is carrying a PREVIOUS INFERENCE, so a
 * reprocess is free to re-infer and improve on it. A sink carrying anything
 * else — `drop-in`, say, which no scan can produce — got it from a person or
 * the catalog, and inference must not overwrite that.
 */
const INFERABLE_SUBTYPES = Object.freeze(['undermount', 'farmhouse']);

/**
 * Rim of a self-rimming (drop-in) sink, proud of the slab it lands on. A
 * real drop-in rim is about a quarter-inch thick. Same number as
 * FARMHOUSE_TOP_PROUD above, deliberately NOT the same constant: that one
 * is a z-fight clearance on an apron, this one is a physical rim.
 */
const DROPIN_RIM_PROUD = 0.25 * IN;
/** Countertop the apron top sits under; the apron drop below the box top. */
const COUNTERTOP_THICKNESS = 1.5 * IN;

function localZDir(rotationY) {
  return { x: Math.sin(rotationY || 0), z: Math.cos(rotationY || 0) };
}

function along(point, dir) {
  return point.x * dir.x + point.z * dir.z;
}

/**
 * Unit XZ direction from the host toward the room. A wall cabinet faces its
 * wall's inward normal; a free-standing host faces its own local +Z, and the
 * scanned sink can only OVERRULE that by sitting clear of the host.
 *
 * That last clause is the correction. The rule used to read "whichever of its
 * two long faces the scanned sink center is nearer", which assumes the host IS
 * the island — one box with two long faces. It no longer is: by the time this
 * runs, `convertFreeStandingStorageToIslands` has split the island into two
 * ROWS and the host is one of them, built with an authoritative front
 * (`atan2(±wideAxisWorld)`, each facing away from the island centre).
 *
 * Measured against a ROW's centre, a normally-seated basin sits a few inches
 * to one side — an 18" bowl in a 24" cabinet, 3.2" off centre on the corpus
 * island — and a bare sign test read that noise as a direction. It flipped the
 * front, `hostFrontAlong` then returned the row's BACK face, and the sink was
 * seated a full row away, in the cabinets opposite (#1891).
 *
 * So the flip now needs EVIDENCE: the sink must sit beyond the host's own
 * half-depth, which no correctly-seated sink does. A sink inside its host says
 * nothing about which face is the front, and the host already knows.
 */
function frontDirFor(host, wall, sinkCenter) {
  if (wall?.rotation?.y !== undefined) return localZDir(wall.rotation.y);
  const dir = localZDir(host.rotation?.y);
  if (!sinkCenter) return dir;
  const toSink = {
    x: sinkCenter.x - host.center.x,
    z: sinkCenter.z - host.center.z,
  };
  const halfDepth = (host.dimensions?.z ?? 0) / 2;
  return along(toSink, dir) < -halfDepth ? { x: -dir.x, z: -dir.z } : dir;
}

/**
 * Does `center` leave the basin inside `host` along the depth axis?
 *
 * A sink has to fit in its own cabinet — a 24" base holds an 18" bowl with
 * inches to spare, and a seat that puts the bowl outside the box it is
 * plumbed into is a contradiction, whatever produced it. The seat is computed
 * from the COUNTER, which on an island is twice a row's depth, so a wrong
 * front direction lands a plausible-looking figure a whole row away; this is
 * the check that says so regardless of how the direction was reached.
 *
 * Tolerance is generous on purpose: this is a contradiction detector, not a
 * placement rule. A bowl a little proud of a scanned box is ordinary; a bowl
 * beyond the box entirely is not.
 */
function seatFitsHost({ center, host, frontDir, depth }) {
  const half = (host?.dimensions?.z ?? 0) / 2;
  if (!(half > 0)) return true;
  const off = along(
    { x: center.x - host.center.x, z: center.z - host.center.z },
    frontDir
  );
  // The bowl must at least OVERLAP the host along the depth axis — its near
  // edge may not be past the far face.
  return Math.abs(off) <= half + (depth ?? 0) / 2;
}

/** The host's front-face coordinate along `frontDir`, preferring its scan box. */
function hostFrontAlong(host, frontDir) {
  const box = host.scanBox?.center && host.scanBox.depth ? host.scanBox : null;
  const center = box ? box.center : host.center;
  const depth = box ? box.depth : (host.dimensions?.z ?? 0);
  return along(center, frontDir) + depth / 2;
}

/**
 * Reveal = host face − scanned sink face, measured toward the room. A real
 * undermount bowl sits several inches behind the doors; the only sink
 * geometry that reaches the face is an apron.
 */
function inferKitchenSinkSubtype({ sink, host, frontDir }) {
  const sinkFront =
    along(sink.center, frontDir) + (sink.dimensions?.z ?? 0) / 2;
  const revealM = hostFrontAlong(host, frontDir) - sinkFront;
  return {
    subtype: revealM <= FARMHOUSE_REVEAL_MAX ? 'farmhouse' : 'undermount',
    revealM,
  };
}

/** Nearest catalog apron width; RoomPlan under-measures, so never below 30". */
function snapFarmhouseWidth(scannedWidthM) {
  if (!Number.isFinite(scannedWidthM)) return FARMHOUSE_WIDTHS_M[1];
  return FARMHOUSE_WIDTHS_M.reduce((best, w) =>
    Math.abs(w - scannedWidthM) < Math.abs(best - scannedWidthM) ? w : best
  );
}

/**
 * Where the sink's front face belongs along `frontDir`, given the counter's
 * front face.
 */
function targetFrontAlong(counterFront, subtype) {
  return subtype === 'farmhouse'
    ? counterFront + APRON_PROUD
    : counterFront - UNDERMOUNT_FRONT_SETBACK;
}

/**
 * Move the sink along the counter's depth axis to its seat and turn it to
 * face the room. Lateral position is preserved (the run-end clamp handles
 * that separately). Returns the new center/rotation plus the signed shift.
 */
function seatSinkOnCounter({ sink, counter, frontDir, subtype, dims }) {
  const counterFront =
    along(counter.center, frontDir) + (counter.dimensions?.z ?? 0) / 2;
  const targetCenter = targetFrontAlong(counterFront, subtype) - dims.depth / 2;
  const shift = targetCenter - along(sink.center, frontDir);
  // Sink local +Z must point along frontDir. The counter's own +Z does
  // unless the sink is on an island's far side, in which case turn it round.
  const counterRy = counter.rotation?.y ?? 0;
  const facesFront = along(localZDir(counterRy), frontDir) > 0;
  return {
    center: {
      ...sink.center,
      x: sink.center.x + frontDir.x * shift,
      z: sink.center.z + frontDir.z * shift,
    },
    rotation: { x: 0, y: facesFront ? counterRy : counterRy + Math.PI, z: 0 },
    seatedInCounterM: shift,
  };
}

/**
 * The front of a farmhouse sink's host cabinet: ONE `farmhouse-front` column
 * spanning the whole box, so the doors below the apron are ordinary doors
 * (a 30" sink in a 36" base gets two 18" doors) — they do NOT line up with
 * the apron, and nothing narrow is emitted beside it (user ruling
 * 2026-09-02). The column draws no false-front strip; its doors start
 * `apronDrop` below the box top, and the renderer closes the band beside the
 * apron with a flat panel.
 *
 * Until now the reveal either side of the apron became its own `doors`
 * column, floored at 1". On Anchorage 2 / Scan 1 a 30" sink in a 36" base
 * produced 3.9" and 2.1" columns, each of which the renderer drew as a real
 * door — four doors in one bay, two of them 2-4" wide. A strip that narrow is
 * a stile or a filler, never a door.
 *
 * `apronWidth` / `apronOffset` (the apron's width and the signed offset of its
 * centre from the box centre, both measured in the cabinet's own frame so a
 * rotated run works) tell the renderer how much of the band the sink's own
 * apron covers. Null for any other sink.
 */
function buildFarmhouseColumns(cabinet, sink) {
  if (sink?.subtype !== 'farmhouse' || !cabinet?.dimensions?.x) return null;
  const cabW = cabinet.dimensions.x;
  const lateral = {
    x: Math.cos(cabinet.rotation?.y ?? 0),
    z: -Math.sin(cabinet.rotation?.y ?? 0),
  };
  const offset = along(
    {
      x: sink.center.x - cabinet.center.x,
      z: sink.center.z - cabinet.center.z,
    },
    lateral
  );
  const apronW = Math.min(sink.dimensions?.x ?? cabW, cabW);
  // Hold the apron inside the box: a basin that measured a little wide, or
  // sits a little off centre, must not report a band the box cannot show.
  const slack = (cabW - apronW) / 2;
  const apronOffset = Math.max(-slack, Math.min(slack, offset));
  return [
    {
      type: 'farmhouse-front',
      width: cabW,
      apronWidth: apronW,
      apronOffset,
      apronDrop:
        (sink.dimensions?.y ?? FARMHOUSE_SINK.height) -
        COUNTERTOP_THICKNESS -
        FARMHOUSE_TOP_PROUD,
    },
  ];
}

/**
 * VERTICAL seat of a kitchen basin: the bbox centre Y for a basin
 * `basinHeight` tall whose countertop's upper surface is at
 * `countertopTopY`. All metres.
 *
 *   undermount  basin TOP coplanar with the slab TOP — **when the box is a
 *               bare bowl**. The slab is NOT cut back to the basin
 *               (`buildKitchenSinkCutout` hands over the basin's own
 *               half-extents and `Countertop.jsx` insets them 1" radially),
 *               so the slab overhangs the basin on every side and hides its
 *               top edge from above. That overhang is what makes it read as
 *               an undermount, so a bare bowl does not also need dropping by
 *               a slab thickness.
 *
 *               `basinBoxIncludesDeck` is the other case, and it is what a
 *               real product is: a GLB's box top is the MOUNTING DECK, not
 *               the bowl rim. Kohler's own bounding box for the Vault 3821
 *               runs -10.08 … +0.05 on its height axis — the deck plane sits
 *               at the very top — so putting that top at the slab top lands
 *               the deck at counter level, which is the drop-in installation,
 *               drawn. Those boxes seat against the slab UNDERSIDE, the way
 *               a real undermount bolts up, exactly as the bathroom twin
 *               already does. The distinction is bowl-box vs product-box, not
 *               kitchen vs bathroom, so the caller states which it holds
 *               rather than the rule guessing from the room.
 *   drop-in     rim LANDS on the slab, so the basin rides one rim-thickness
 *               higher. Seated as an undermount its rim would sit beneath
 *               that same 1" overhang, which is an undermount with extra
 *               steps. The cutout needs no drop-in case: the hole the
 *               renderer ends up with is already an inch inside the rim on
 *               every side, which is exactly where a drop-in's body passes
 *               through.
 *   farmhouse   apron stands FARMHOUSE_TOP_PROUD above the slab (a coplanar
 *               top z-fights it) and opens the cabinet front.
 *
 * The BATHROOM twin — `vanity-assembly/calculateSinkSeatY` — has only ever
 * seen product boxes, since bathroom sinks are catalog GLBs, and seats an
 * undermount against the slab underside. So the two rules now AGREE wherever
 * both are handed the same kind of box, and they still differ on the bare
 * bowl, which only the kitchen has. Sharing one function outright would move
 * all 50 procedural kitchen basins in dev; keeping two that disagree by ROOM
 * is what put a product's mounting deck at counter level.
 *
 * Pure arithmetic on values every caller already holds as constants, so
 * there is no not-a-number branch: bad input produces NaN here exactly as it
 * did when this expression was inline.
 */
function kitchenSinkSeatY({
  subtype,
  countertopTopY,
  countertopBottomY,
  basinHeight,
  basinBoxIncludesDeck = false,
}) {
  if (subtype === 'undermount' && basinBoxIncludesDeck) {
    return countertopBottomY - basinHeight / 2;
  }
  return countertopTopY - basinHeight / 2 + topProudFor(subtype);
}

/**
 * What a kitchen sink PRODUCT can be installed as.
 *
 * The importer classifies each model's geometry once and stores the answer as
 * `kitchenSinkSubtype` on its catalog doc — `'farmhouse'` when it finds an
 * apron across the front face, `'undermount-or-dropin'` when it does not,
 * because a rimmed bowl with no apron is genuinely BOTH (Kohler sells the
 * Vault as "top-mount/undermount"). That field is capability; the object's
 * own `subtype` is the install that was CHOSEN. Two different facts, and the
 * schema already separated them — nothing had read the capability yet.
 *
 * @returns {string[]} installs this model supports, best default first.
 */
function kitchenSinkInstallOptions(modelDoc) {
  return modelDoc?.kitchenSinkSubtype === 'farmhouse'
    ? ['farmhouse']
    : ['undermount', 'drop-in'];
}

/**
 * The install to seat a swapped-in kitchen sink as.
 *
 * **Undermount is the default**, because it is the first option above — a
 * rimless look is what a kitchen sink is expected to be unless someone says
 * otherwise. An existing choice survives a re-swap only when the new model can
 * still honour it: dropping a farmhouse model onto a sink recorded as
 * `undermount` has to become `farmhouse`, since an apron front is not an
 * installation option, it is the shape of the product.
 *
 * Without this the resolution ran `getSinkType(model) || getSinkType(sink)`,
 * and a kitchen catalog doc carries neither `sinkType` nor `subtype` — so the
 * first term was always null and the OLD sink's install was always kept. It
 * looked right only because almost every kitchen sink starts undermount.
 */
function resolveKitchenSinkInstall({ modelDoc, current }) {
  const options = kitchenSinkInstallOptions(modelDoc);
  return options.includes(current) ? current : options[0];
}

/** How far a subtype's top stands above the slab. */
function topProudFor(subtype) {
  if (subtype === 'farmhouse') return FARMHOUSE_TOP_PROUD;
  // `sink-types` spells the kitchen one `drop-in` and the bathroom one
  // `dropin`; a sink crossing that boundary must not fall through to the
  // undermount branch unnoticed.
  if (subtype === 'drop-in' || subtype === 'dropin') return DROPIN_RIM_PROUD;
  return 0;
}

module.exports = {
  FARMHOUSE_SINK,
  FARMHOUSE_WIDTHS_M,
  FARMHOUSE_REVEAL_MAX,
  UNDERMOUNT_FRONT_SETBACK,
  APRON_PROUD,
  FARMHOUSE_TOP_PROUD,
  DROPIN_RIM_PROUD,
  INFERABLE_SUBTYPES,
  kitchenSinkSeatY,
  kitchenSinkInstallOptions,
  resolveKitchenSinkInstall,
  frontDirFor,
  seatFitsHost,
  targetFrontAlong,
  inferKitchenSinkSubtype,
  snapFarmhouseWidth,
  seatSinkOnCounter,
  buildFarmhouseColumns,
};
